import { useCallback, useState } from "react";
import type { PopoverOrigin } from "@mui/material/Popover";

const VIEWPORT_GUTTER = 16;
// 6〜7件程度をそのまま表示できる高さ。これだけ下に空きがあれば、上側がより広くても下を優先する。
const PREFERRED_MENU_HEIGHT = 240;

export type MenuVerticalPlacement = "top" | "bottom";

interface ResponsiveMenuPlacement {
  vertical: MenuVerticalPlacement;
  maxHeight: number;
}

interface ResponsiveMenuPlacementResult {
  menuMaxHeight: number;
  menuAnchorOrigin: PopoverOrigin;
  menuTransformOrigin: PopoverOrigin;
  popperPlacement: "bottom-start" | "top-start";
  updateMenuPlacement: (anchorElement: HTMLElement | null) => void;
}

/** トリガー上下の実空き領域に合わせ、メニューの方向と最大高を決める。 */
export const useResponsiveMenuPlacement = (
  defaultMaxHeight: number,
): ResponsiveMenuPlacementResult => {
  const [placement, setPlacement] = useState<ResponsiveMenuPlacement>({
    vertical: "bottom",
    maxHeight: defaultMaxHeight,
  });

  const updateMenuPlacement = useCallback(
    (anchorElement: HTMLElement | null) => {
      if (!anchorElement || typeof window === "undefined") return;

      const rect = anchorElement.getBoundingClientRect();
      const availableAbove = Math.max(rect.top - VIEWPORT_GUTTER, 0);
      const availableBelow = Math.max(
        window.innerHeight - rect.bottom - VIEWPORT_GUTTER,
        0,
      );
      const requiredHeight = Math.min(defaultMaxHeight, PREFERRED_MENU_HEIGHT);
      const vertical =
        availableBelow >= requiredHeight
          ? "bottom"
          : availableAbove >= requiredHeight
            ? "top"
            : availableBelow >= availableAbove
              ? "bottom"
              : "top";
      const availableHeight =
        vertical === "bottom" ? availableBelow : availableAbove;

      setPlacement({
        vertical,
        // 最低高は強制しない。空きが小さい時も他フォームに重ねない。
        maxHeight: Math.min(defaultMaxHeight, Math.max(availableHeight, 1)),
      });
    },
    [defaultMaxHeight],
  );

  const anchorVertical: "top" | "bottom" =
    placement.vertical === "bottom" ? "bottom" : "top";
  const transformVertical: "top" | "bottom" =
    placement.vertical === "bottom" ? "top" : "bottom";

  return {
    menuMaxHeight: placement.maxHeight,
    menuAnchorOrigin: {
      vertical: anchorVertical,
      horizontal: "left",
    },
    menuTransformOrigin: {
      vertical: transformVertical,
      horizontal: "left",
    },
    popperPlacement:
      placement.vertical === "bottom"
        ? ("bottom-start" as const)
        : ("top-start" as const),
    updateMenuPlacement,
  };
};
